'use client';

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ChangeTicketModal from '@/components/modals/ChangeTicketModal';
import TestNotIncludedNotice from '@/components/tests/TestNotIncludedNotice';
import type { TestNotIncludedItem } from '@/lib/tests/testNotIncluded';

interface TestNotIncludedNoticesProps {
  items: ReadonlyArray<TestNotIncludedItem>;
  /** List pages name the event on each notice; the event page does not. */
  showEventTitle?: boolean;
  /** Called after a successful ticket change, for the caller's own local state. */
  onUpgraded?: () => void;
}

/**
 * The notices for a set of TestNotIncludedItems plus the ticket-change modal
 * their "Upgrade ticket" button opens. Same component on every surface.
 */
export default function TestNotIncludedNotices({ items, showEventTitle = false, onUpgraded }: TestNotIncludedNoticesProps) {
  const [upgrading, setUpgrading] = useState<TestNotIncludedItem | null>(null);
  const queryClient = useQueryClient();
  if (items.length === 0) return null;

  return (
    <>
      <div className="space-y-3">
        {items.map((item) => (
          <TestNotIncludedNotice
            key={item.enrollmentId}
            eventTitle={showEventTitle ? item.contentTitle : undefined}
            eventHref={showEventTitle ? `/${item.contentType === 'course' ? 'courses' : 'event'}/${item.contentId}` : undefined}
            ticketName={item.ticket?.name}
            openUntil={item.openUntil}
            onUpgrade={item.canUpgrade && item.ticket ? () => setUpgrading(item) : undefined}
            organizerEmail={item.organizerEmail}
          />
        ))}
      </div>
      {upgrading?.ticket && (
        <ChangeTicketModal
          isOpen={true}
          onClose={() => setUpgrading(null)}
          enrollmentId={upgrading.enrollmentId}
          contentId={upgrading.contentId}
          currentTicket={upgrading.ticket}
          paidAmount={upgrading.paidAmount}
          onSuccess={() => {
            setUpgrading(null);
            // Every surface's notices and the dashboard pending list change together.
            queryClient.invalidateQueries({ queryKey: ['test-not-included'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard'] });
            onUpgraded?.();
          }}
        />
      )}
    </>
  );
}
